import type { InsertEntityMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export type InsertEntityTRPCInput = {
  Name: string;
  Description?: string | null;
  ParentId?: string | null;
  Weight: number;
  OwnerUserIds?: string[] | null;
  OwnerGroupIds?: string[] | null;
};

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): InsertEntityMutation => {
  return {
    insertChildEntity: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
};

export const useInsertEntityTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.entity.create.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.entity.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.entity.getById.queryKey(),
        });
      },
    }),
  });

  return {
    insertEntity: async (
      variables: InsertEntityTRPCInput
    ): Promise<InsertEntityMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
